/**
 * TEF Canada written expression (D-109): section A (continue a news story, 80 words at least,
 * 25 minutes) then section B (argue a point of view, 200 words at least, 35 minutes), one hour
 * in all. The French editor (accent keys), a live word counter against the minimum, automatic
 * saving while typing, a timer per section. As at the centre (« durée limitée par section »,
 * D-135), section A closes after its 25 minutes: what was typed is handed in and section B opens.
 */

import { ApiError, academyAttemptsNextCreate, academyAttemptsTextCreate } from "@agencyos/api-client";
import { type TefState, writingPlan } from "@agencyos/core";
import { Badge, Button } from "@agencyos/ui-web";
import { ArrowUpFromLine, Check, Clock } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { WritingField } from "../ielts/ItemInput";
import { countWords } from "../text";

type SaveState = "idle" | "saving" | "saved" | "error";

export function TefWriting({ attemptId, state, onChanged }: { attemptId: string; state: TefState; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const tasks = state.tasks;
  const [texts, setTexts] = useState<Record<string, string>>(() => Object.fromEntries(tasks.map((task) => [task.id, task.text])));
  const [saves, setSaves] = useState<Record<string, SaveState>>({});
  const [current, setCurrent] = useState(() => Math.max(0, tasks.findIndex((task) => task.status !== "submitted" && task.status !== "graded")));
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [spent, setSpent] = useState(0);
  const timers = useRef(new Map<string, number>());
  const latest = useRef(texts);
  const queue = useRef<Promise<void>>(Promise.resolve());

  // The section timers start from the time already spent in the section (server clock).
  const minutesOf = (n: number) => state.tefTasks.find((item) => item.section === (n === 1 ? "A" : "B"))?.minutes ?? 0;
  const minutesA = minutesOf(1);
  const minutesB = minutesOf(2);
  const total = (minutesA + minutesB) * 60;
  useEffect(() => {
    setSpent(Math.max(total - state.remaining, 0));
    const timer = window.setInterval(() => setSpent((s) => s + 1), 1000);
    return () => window.clearInterval(timer);
  }, [total, state.remaining]);
  const plan = writingPlan(spent, { A: minutesA, B: minutesB });

  useEffect(() => {
    latest.current = texts;
  }, [texts]);

  const save = useCallback(
    (taskId: string, submit = false): Promise<void> => {
      window.clearTimeout(timers.current.get(taskId));
      timers.current.delete(taskId);
      queue.current = queue.current.then(async () => {
        setSaves((s) => ({ ...s, [taskId]: "saving" }));
        try {
          await academyAttemptsTextCreate(attemptId, { task: taskId, text: latest.current[taskId] ?? "", submit });
          setSaves((s) => ({ ...s, [taskId]: "saved" }));
        } catch (failure) {
          setSaves((s) => ({ ...s, [taskId]: "error" }));
          if (failure instanceof ApiError && failure.status === 400) await onChanged();
        }
      });
      return queue.current;
    },
    [attemptId, onChanged],
  );

  const flushAll = useCallback(async () => {
    await Promise.all([...timers.current.keys()].map((taskId) => save(taskId)));
  }, [save]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void flushAll();
    };
    document.addEventListener("visibilitychange", onHide);
    const pending = timers.current;
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      // D-171: the section closes at the end of the time: what was just typed is still sent
      [...pending.keys()].forEach((taskId) => void save(taskId));
    };
  }, [flushAll, save]);

  const onText = (taskId: string, text: string) => {
    setTexts((prev) => ({ ...prev, [taskId]: text }));
    latest.current = { ...latest.current, [taskId]: text };
    window.clearTimeout(timers.current.get(taskId));
    timers.current.set(taskId, window.setTimeout(() => void save(taskId), 1500));
  };

  const submitTask = async (taskId: string) => {
    await save(taskId, true);
    await onChanged();
    const next = tasks.findIndex((task, i) => i > current && task.status !== "submitted");
    if (next >= 0) setCurrent(next);
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await flushAll();
      await academyAttemptsNextCreate(attemptId);
    } finally {
      await onChanged();
      setFinishing(false);
    }
  };

  // D-135: at the end of its 25 minutes, section A is handed in by itself (the server refuses it
  // after that time too) and the learner goes on with section B.
  const closingA = useRef(false);
  useEffect(() => {
    const first = tasks.find((item) => item.number !== 2);
    if (!plan.overA || !first || closingA.current) return;
    if (first.status !== "" && first.status !== "draft") return;
    closingA.current = true;
    void submitTask(first.id);
  }, [plan.overA, tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const task = tasks[current];
  const info = state.tefTasks.find((item) => item.id === task?.id);
  const letter = info?.section || (task?.number === 2 ? "B" : "A");
  const locked = task ? task.status !== "" && task.status !== "draft" : true;
  const saveState = task ? (saves[task.id] ?? "idle") : "idle";
  const onWrongSection = plan.overA && letter === "A" && !locked;

  return (
    <section className="ac-writing" aria-label={t("tef.skill.ee")}>
      <p className="ac-note" role="timer" aria-live="polite">
        <Clock aria-hidden size={14} /> {t("tef.writing.plan", { section: plan.section, count: plan.left })}
      </p>
      <div className="ac-task-tabs" role="tablist" aria-label={t("academy.writing.tasks")}>
        {tasks.map((item, index) => {
          const sent = item.status === "submitted" || item.status === "graded";
          const itemLetter = item.number === 2 ? "B" : "A";
          return (
            <button key={item.id} type="button" role="tab" className={`ac-task-tab${sent ? " ac-task-tab--sent" : ""}`} aria-selected={index === current} onClick={() => setCurrent(index)}>
              <span className="ac-task-tab__n ao-num" aria-hidden="true">
                {sent ? <Check size={13} strokeWidth={3} /> : itemLetter}
              </span>
              <span>{t("tef.writing.section", { section: itemLetter })}</span>
              <span className="ac-task-tab__words ao-num" aria-hidden="true">
                {t("academy.writing.wordsPlain", { count: countWords(texts[item.id] ?? "") })}
              </span>
            </button>
          );
        })}
      </div>

      <div className="ac-question__sheet">
        {task ? (
          <div className="ac-task" key={task.id}>
            <div className="ac-task__brief">
              <p className="ac-player__eyebrow">{t(letter === "A" ? "tef.writing.aTitle" : "tef.writing.bTitle")}</p>
              <div className="ac-ielts-meta">
                {task.minWords !== null && <Badge tone="accent">{t("tef.writing.minWords", { count: task.minWords })}</Badge>}
                <span className="ac-caption">
                  <Clock aria-hidden size={13} /> {t("tef.writing.minutes", { count: letter === "A" ? minutesA : minutesB })}
                </span>
              </div>
              {(info?.documents ?? []).map((doc, index) => (
                <blockquote key={index} className="ac-tef-document" lang="fr">
                  {doc.title && <strong className="ac-tef-document__title">{doc.title}</strong>}
                  <p>{doc.text}</p>
                </blockquote>
              ))}
              <p className="ac-prompt ac-ielts-prompt" lang="fr">
                {task.prompt}
              </p>
              <p className="ac-footnote">{t(letter === "A" ? "tef.writing.aTip" : "tef.writing.bTip")}</p>
            </div>
            {onWrongSection && (
              <p className="ac-note ac-note--warning" role="status">
                {t("tef.writing.moveToB")}
              </p>
            )}
            <WritingField
              value={texts[task.id] ?? ""}
              onChange={(text) => onText(task.id, text)}
              minWords={task.minWords}
              disabled={locked}
              rows={14}
              label={t("tef.writing.answer", { section: letter })}
            />
            <p className={`ac-save-state ac-save-state--${locked ? "saved" : saveState}`} aria-live="polite">
              <span className="ac-dot" aria-hidden="true" />
              {locked ? t("academy.writing.submitted") : t(`academy.writing.save.${saveState}`)}
            </p>
            {!locked && (
              <div className="ac-question__actions">
                <Button variant="secondary" icon={<ArrowUpFromLine aria-hidden size={18} />} onClick={() => void submitTask(task.id)}>
                  {t("tef.writing.submit", { section: letter })}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="ac-secondary">{t("academy.writing.noTask")}</p>
        )}
      </div>

      <div className="ac-finish">
        {confirmFinish ? (
          <div className="ac-finish__confirm" role="alertdialog" aria-label={t("tef.writing.finish")}>
            <p>{t("tef.writing.finishConfirm")}</p>
            <div className="ac-card__actions">
              <Button variant="ghost" onClick={() => setConfirmFinish(false)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={() => void finish()} disabled={finishing}>
                {finishing ? t("common.loading") : t("tef.writing.finish")}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" onClick={() => setConfirmFinish(true)}>
            {t("tef.writing.finish")}
          </Button>
        )}
      </div>
    </section>
  );
}
